<script setup>
import { computed, inject, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useConfigStore } from "@/store/config";
import { useRefStore } from "@/store/ref";
import { useUserStore } from "@/store/user";
import { claimDividends } from "@/api/TON";
import IndexShare from "@/components/Share/index.vue";
import { appHapticFeedback, toastFail, toastSuccess, addCommasToNumber } from "@/utils/common";

const props = defineProps({
  balanceTarget: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();
const $swal = inject("$swal");
const config = useConfigStore();
const refStore = useRefStore();
const userStore = useUserStore();

const claimLoading = ref(false);
const claimBtnRef = ref(null);
const dividendsInfoVisible = ref(false);
let claimStoreUpdateTimer = null;

const dividends = computed(() => Number(userStore.dividends) || 0);
const shareUrl = computed(() => config.app_link);
const shareTitle = computed(() => config.share_title);
const shareTwaid = computed(() => userStore.twa_id);
const dividendsDesc = computed(() => {
  return (
    config.superiors_dividends_desc ||
    translateOrFallback(
      "message.dividendsInfoFallback",
      "Invite friends to earn referral rewards",
    )
  );
});

function translateOrFallback(key, fallback) {
  const translated = t(key);
  return translated === key ? fallback : translated;
}

function showDividendsInfo() {
  appHapticFeedback();
  dividendsInfoVisible.value = true;
}

function closeDividendsInfo() {
  appHapticFeedback();
  dividendsInfoVisible.value = false;
}

async function handleClaim() {
  appHapticFeedback();

  if (claimLoading.value) {
    return;
  }

  if (dividends.value <= 0) {
    toastFail(
      $swal,
      translateOrFallback("message.noDividends", "No dividends to claim"),
    );
    return;
  }

  claimLoading.value = true;

  try {
    const res = await claimDividends();
    if (res.code === 200 && res.data) {
      const nextBalance = Number(res.data.balance);
      const nextDividends = Number(res.data.dividends) || 0;
      const canAnimate =
        refStore.flyingIconsRef &&
        claimBtnRef.value &&
        props.balanceTarget &&
        config.coin_image;

      if (canAnimate) {
        refStore.flyingIconsRef.triggerAnimation(
          claimBtnRef.value,
          props.balanceTarget,
          config.coin_image,
          12,
        );
      }

      const commitStoreUpdate = () => {
        userStore.balance = Number.isFinite(nextBalance)
          ? nextBalance
          : userStore.balance;
        userStore.dividends = nextDividends;
        claimStoreUpdateTimer = null;
      };

      if (canAnimate) {
        claimStoreUpdateTimer = setTimeout(commitStoreUpdate, 300);
      } else {
        commitStoreUpdate();
      }

      toastSuccess(
        $swal,
        translateOrFallback("message.claimSuccess", "Claimed successfully"),
      );
      dividendsInfoVisible.value = false;
    } else {
      toastFail(
        $swal,
        res.message ||
          translateOrFallback("message.claimFailed", "Claim failed, please try again"),
      );
    }
  } catch (error) {
    console.error("Claim dividends failed:", error);
    toastFail(
      $swal,
      translateOrFallback("message.claimFailed", "Claim failed, please try again"),
    );
  } finally {
    claimLoading.value = false;
  }
}

onUnmounted(() => {
  if (claimStoreUpdateTimer) {
    clearTimeout(claimStoreUpdateTimer);
  }
});
</script>

<template>
  <div class="dividends-card">
    <div class="dividends-top">
      <div class="dividends-label">
        {{ translateOrFallback("message.Earnings", "Earnings") }}
      </div>
      <button type="button" class="dividends-link" @click="showDividendsInfo">
        {{ translateOrFallback("message.learnMore", "Learn more") }}
      </button>
    </div>

    <div class="dividends-row">
      <div class="dividends-amount">{{ addCommasToNumber(dividends.toFixed(3)) }}</div>
      <button
        ref="claimBtnRef"
        class="claim-btn"
        :disabled="dividends <= 0 || claimLoading"
        @click="handleClaim"
      >
        <span v-if="!claimLoading">{{ $t("message.Claim") }}</span>
        <span v-else class="claim-btn-loading">
          <svg class="claim-spinner" width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 2a10 10 0 0 1 10 10"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
            />
          </svg>
        </span>
      </button>
    </div>
  </div>

  <div
    v-if="dividendsInfoVisible"
    class="dividends-info-overlay"
    @click.self="closeDividendsInfo"
  >
    <div class="dividends-info-popup">
      <div class="dividends-info-header">
        <div class="dividends-info-title">
          {{ translateOrFallback("message.Earnings", "Earnings") }}
        </div>
        <button type="button" class="dividends-info-close" @click="closeDividendsInfo">
          ×
        </button>
      </div>

      <div class="dividends-info-body">{{ dividendsDesc }}</div>

      <IndexShare
        :url="shareUrl"
        :title="shareTitle"
        :twa_id="shareTwaid"
        :long_btn="true"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "@/styles/surfaces" as surface;

.dividends-card {
  @include surface.card;
  gap: 4px;
  width: 100%;
  background: linear-gradient(135deg, #171717 0%, #232323 100%);
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow: 0 14px 32px rgba(0, 0, 0, 0.24);
}

.dividends-top,
.dividends-row,
.dividends-info-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}

.dividends-label,
.dividends-info-title {
  color: #ffffff;
  font-size: var(--home-font-title, 16px);
  line-height: 1.4;
  font-weight: 700;
}

.dividends-link,
.dividends-info-close,
.claim-btn {
  border: none;
  cursor: pointer;
}

.dividends-link {
  @include surface.press-feedback;
  min-height: 28px;
  padding: 4px 0;
  background: transparent;
  color: #b8bcc8;
  font-size: var(--home-font-caption, 13px);
  font-family: inherit;
  line-height: 1.5;
  text-decoration: underline;
  text-underline-offset: 3px;
  flex-shrink: 0;
}

.dividends-amount {
  font-size: 24px;
  color: #ffffff;
  min-width: 0;
  max-width: 100%;
  line-height: 1.3;
  overflow-wrap: anywhere;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.claim-btn {
  @include surface.button;
  min-width: 88px;
  max-width: 100%;
  margin-left: auto;
  color: #ffffff;
  font-weight: 700;
  background: v-bind("config.themes_color");
}

.claim-btn:disabled {
  opacity: 0.45;
  cursor: default;
}

.claim-btn-loading {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.claim-spinner {
  animation: claim-spin 0.8s linear infinite;
}

.dividends-info-overlay {
  @include surface.dialog-overlay;
  position: fixed;
  inset: 0;
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(6px);
}

.dividends-info-popup {
  @include surface.dialog-panel;
  background: #18181b;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);
}

.dividends-info-close {
  @include surface.close-button;
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
}

.dividends-info-body {
  margin: 8px 0;
  line-height: 1.6;
  color: #f5f5f5;
  font-size: 14px;
  white-space: pre-wrap;
  word-break: break-word;
}

@keyframes claim-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
